/**
 * video-editor/redact.js — скрытие участков кадра (номера, лица, адреса).
 *
 * ── Зачем это в редакторе, а не отдельным тулом ────────────────────────────
 * Готовя запись видеорегистратора для страховой, ГИБДД или для публикации,
 * человек делает три вещи подряд: вырезает нужные секунды, закрывает чужие
 * номера и лица, ужимает под предел формы загрузки. Первое и третье редактор
 * уже умеет — не хватало ровно второго, и из-за него весь маршрут разваливался
 * на три инструмента с промежуточными файлами между ними.
 *
 * ── Почему по умолчанию ПИКСЕЛИЗАЦИЯ, а не размытие ────────────────────────
 * Размытие выглядит аккуратнее и потому кажется очевидным выбором. Но гауссово
 * размытие — обратимая свёртка: при известном радиусе часть информации
 * восстанавливается, и для номерного знака с его десятком знаков этого иногда
 * достаточно. Пикселизация же усредняет блок в одно значение и восстановлению
 * не поддаётся в принципе. Разница видна не глазом, а в том, что произойдёт с
 * файлом дальше, — поэтому умолчание здесь про необратимость, а размытие
 * оставлено для случаев, где скрывают не текст.
 *
 * ── Ключевые точки, а не автослежение ──────────────────────────────────────
 * Автоматическое слежение за областью живёт в /video-blur и стоит отдельного
 * прохода по кадрам. Здесь оно было бы лишним: у записи для страховой длина
 * в десятки секунд, а объект движется по кадру плавно, поэтому две-три
 * поставленных руками точки описывают его путь точнее любого трекера — и,
 * главное, предсказуемо. Между точками положение и размер интерполируются
 * линейно.
 *
 * ⚠️ Модуль вызывается ОДНИМ И ТЕМ ЖЕ кодом из превью и из экспорта (крючок
 * onOverlay). Разойтись им нельзя: расхождение того, что человек видел, и
 * того, что ушло в файл, — самая дорогая из тихих поломок, а здесь она ещё и
 * означает, что чужой номер уехал в страховую нескрытым.
 */
(function () {
'use strict';

if (window.VideoEditorRedact) return;

function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

/** Ключевые точки по времени — источник истины о положении области. */
function sortedKeys(region) {
    var k = (region.keys || []).slice();
    k.sort(function (a, b) { return a.t - b.t; });
    return k;
}

/**
 * Положение области в момент t.
 * Возвращает {x,y,w,h} в долях кадра или null, если область в этот момент
 * не показывается.
 */
function rectAt(region, t) {
    if (!(t >= region.start - 1e-6 && t <= region.end + 1e-6)) return null;
    var keys = sortedKeys(region);
    if (!keys.length) return null;
    if (keys.length === 1 || t <= keys[0].t) return pick(keys[0]);
    if (t >= keys[keys.length - 1].t) return pick(keys[keys.length - 1]);

    for (var i = 0; i < keys.length - 1; i++) {
        var a = keys[i], b = keys[i + 1];
        if (t >= a.t && t <= b.t) {
            var span = (b.t - a.t) || 1e-6;
            var f = (t - a.t) / span;
            return {
                x: a.x + (b.x - a.x) * f,
                y: a.y + (b.y - a.y) * f,
                w: a.w + (b.w - a.w) * f,
                h: a.h + (b.h - a.h) * f,
            };
        }
    }
    return pick(keys[keys.length - 1]);
}

function pick(k) { return { x: k.x, y: k.y, w: k.w, h: k.h }; }

/** Все области, видимые в момент t, с уже посчитанной геометрией. */
function at(regions, t) {
    var out = [];
    for (var i = 0; i < (regions || []).length; i++) {
        var r = rectAt(regions[i], t);
        if (r) out.push({ rect: r, mode: regions[i].mode || 'pixelate', strength: regions[i].strength || 6, index: i });
    }
    return out;
}

/**
 * Наложить скрытие на уже нарисованный кадр.
 *
 * @param {CanvasRenderingContext2D} ctx  контекст с НАРИСОВАННЫМ кадром
 * @param {Array} items                   результат at()
 * @param {number} W @param {number} H    размеры холста в пикселях
 */
function apply(ctx, items, W, H) {
    if (!items || !items.length) return;
    for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var x = Math.round(clamp(it.rect.x, 0, 1) * W);
        var y = Math.round(clamp(it.rect.y, 0, 1) * H);
        var w = Math.round(clamp(it.rect.w, 0, 1) * W);
        var h = Math.round(clamp(it.rect.h, 0, 1) * H);
        // Область меньше пикселя ничего не скрывает, а деление на ноль ниже
        // роняет весь кадр — и превью, и экспорт разом.
        if (w < 2 || h < 2) continue;
        x = clamp(x, 0, W - 2); y = clamp(y, 0, H - 2);
        w = Math.min(w, W - x); h = Math.min(h, H - y);

        if (it.mode === 'blur') blurRegion(ctx, x, y, w, h, it.strength);
        else pixelateRegion(ctx, x, y, w, h, it.strength);
    }
}

/**
 * Пикселизация: усреднение блока в одно значение. Необратимо.
 *
 * ⚠️ Сила задаёт ЧИСЛО БЛОКОВ поперёк меньшей стороны области, а не размер
 * блока в пикселях. Это не косметика: блок в «столько-то пикселей» скрывает
 * тем хуже, чем больше разрешение вывода, — один и тот же номер на 720p был
 * бы замазан, а на 4K остался бы читаемым при тех же настройках. Первая
 * реализация считала именно так, и вдобавок ползунок работал наоборот:
 * максимальная сила давала САМЫЕ МЕЛКИЕ блоки (замер на настоящем экспорте —
 * перепад яркости внутри области падал всего на 28 %).
 *
 * Три блока поперёк — потолок: дальше область превращается в плашку, что
 * скрывает не лучше, а выглядит как дефект записи.
 */
function pixelateRegion(ctx, x, y, w, h, strength) {
    var cells = clamp(Math.round(13 - strength), 3, 12);     // сила 1 → 12 блоков, 10 → 3
    var block = Math.max(1, Math.min(w, h) / cells);         // блоки квадратные
    var sw = Math.max(1, Math.round(w / block));
    var sh = Math.max(1, Math.round(h / block));

    var tmp = document.createElement('canvas');
    tmp.width = Math.max(1, sw); tmp.height = Math.max(1, sh);
    var tc = tmp.getContext('2d');
    tc.imageSmoothingEnabled = true;                          // усреднение при уменьшении
    tc.drawImage(ctx.canvas, x, y, w, h, 0, 0, tmp.width, tmp.height);

    ctx.save();
    ctx.imageSmoothingEnabled = false;                        // обратно — БЕЗ сглаживания
    ctx.drawImage(tmp, 0, 0, tmp.width, tmp.height, x, y, w, h);
    ctx.restore();
}

/** Размытие — для случаев, где скрывают не текст (лицо, интерьер, документ). */
function blurRegion(ctx, x, y, w, h, strength) {
    var r = Math.max(2, Math.round(Math.min(w, h) / 10 * (strength / 5)));
    /* Поле ниже задумывалось как защита края, но копия бралась БЕЗ него
       (исходный прямоугольник pad,pad,w,h), и пятно к краю выцветало поверх
       исходника: у края оставалось ~126 из 255 уровней полос, во всех
       браузерах (замер 27.09.2026). blurHide берёт поле ≥ 3σ настоящих
       соседей (за краем кадра — повтор крайних пикселей) и заменяет область. */
    if (window.CanvasCssFilter && window.CanvasCssFilter.blurHide) {
        window.CanvasCssFilter.blurHide(ctx, null, x, y, w, h, r);
        return;
    }
    var tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    var tc = tmp.getContext('2d');
    /* Рисуем с запасом по краям и затем возвращаем только середину: без этого
       края области размываются «в прозрачность» и остаются читаемыми ровно
       там, где чаще всего и находится край номерного знака. */
    var pad = r * 2;
    var big = document.createElement('canvas');
    big.width = w + pad * 2; big.height = h + pad * 2;
    var bc = big.getContext('2d');
    bc.drawImage(ctx.canvas,
        clamp(x - pad, 0, ctx.canvas.width), clamp(y - pad, 0, ctx.canvas.height),
        w + pad * 2, h + pad * 2, 0, 0, w + pad * 2, h + pad * 2);
    tc.filter = 'blur(' + r + 'px)';
    tc.drawImage(big, pad, pad, w, h, 0, 0, w, h);
    tc.filter = 'none';
    ctx.drawImage(tmp, x, y);
}

/** Рамка выбранной области — только для превью, в файл не попадает. */
function outline(ctx, items, W, H, selectedIndex) {
    ctx.save();
    for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var x = it.rect.x * W, y = it.rect.y * H, w = it.rect.w * W, h = it.rect.h * H;
        var on = (it.index === selectedIndex);
        ctx.setLineDash(on ? [] : [Math.max(3, W / 160), Math.max(3, W / 220)]);
        ctx.strokeStyle = on ? 'rgba(96,165,250,0.95)' : 'rgba(255,255,255,0.55)';
        ctx.lineWidth = Math.max(1.5, W / 340);
        ctx.strokeRect(x, y, w, h);
        if (on) {
            // Уголок для изменения размера — там же, где его ищут пальцем.
            var s = Math.max(8, W / 60);
            ctx.fillStyle = 'rgba(96,165,250,0.95)';
            ctx.fillRect(x + w - s, y + h - s, s, s);
        }
    }
    ctx.restore();
}

window.VideoEditorRedact = { at: at, apply: apply, outline: outline, rectAt: rectAt };
})();
